/**
 * @title Bulk Upload API Service
 * @description Service for bulk upload operations on credit limits
 * @version 1.0
 */

import { API_URLS } from '../utils/config';
import { request } from '../utils/request';

const URL = API_URLS.THIRD_PARTY;

export interface AbstractResponse<D, L> {
  code: string;
  data: D;
  description: string;
  list: Array<L>;
  message: string;
}

export interface BulkUploadResult {
  totalLines: number;
  processedLines: number;
  successLines: number;
  errorLines: number;
  fileName: string;
  processingTime: string;
  errors: BulkUploadError[];
}

export interface BulkUploadError {
  lineNumber: number;
  errorMessage: string;
  lineContent: string;
}

/**
 * @tags Bulk Upload Controller
 * @summary Download Bulk Upload Template
 */
export async function downloadBulkUploadTemplate(): Promise<AbstractResponse<Blob, any>> {
  const response = await fetch(`${URL}/api/bulk-limits/template`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Dev-Stage': 'true'
    },
    mode: 'cors'
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const blob = await response.blob();

  return {
    code: '200',
    data: blob,
    description: 'Template downloaded successfully',
    list: [],
    message: 'Success'
  };
}

/**
 * @tags Bulk Upload Controller
 * @summary Process Bulk Credit Limits Upload
 */
export async function processBulkUpload(file: File): Promise<AbstractResponse<Blob, any>> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${URL}/api/bulk-limits/process`, {
    method: 'POST',
    body: formData,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Dev-Stage': 'true'
    },
    mode: 'cors'
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const blob = await response.blob();

  return {
    code: '200',
    data: blob,
    description: 'File processed successfully',
    list: [],
    message: 'Success'
  };
}

/**
 * Utility functions for bulk upload operations
 */
export const bulkUploadUtils = {
  /**
   * Validates if the file is a valid CSV file
   */
  validateFile: (file: File): { isValid: boolean; error?: string } => {
    // Vérifier l'extension (accepter CSV et Excel)
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExtension) {
      return { isValid: false, error: 'Le fichier doit être au format CSV (.csv) ou Excel (.xlsx, .xls)' };
    }

    const validTypes = [
      'text/csv', 
      'application/csv', 
      'text/plain', 
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    if (file.type && !validTypes.includes(file.type)) {
      console.warn(`Type MIME inattendu: ${file.type}, mais extension valide détectée`);
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return { isValid: false, error: 'La taille du fichier ne peut pas dépasser 10MB' };
    }

    if (file.size === 0) {
      return { isValid: false, error: 'Le fichier ne peut pas être vide' };
    }

    return { isValid: true };
  },

  /**
   * Downloads a blob as a file
   */
  downloadBlob: (blob: Blob, filename: string): void => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Nettoyer l'URL après un délai
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      throw new Error('Impossible de télécharger le fichier');
    }
  },

  /**
   * Formats file size in human readable format
   */
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Checks if file extension is CSV
   */
  isCSVFile: (fileName: string): boolean => {
    return fileName.toLowerCase().endsWith('.csv');
  }
};

// Export des fonctions principales pour compatibilité
export const bulkUploadApi = {
  downloadTemplate: downloadBulkUploadTemplate,
  processFile: processBulkUpload,
  validateFile: bulkUploadUtils.validateFile,
  downloadBlob: bulkUploadUtils.downloadBlob
};